import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { UdvegaLogo } from '../components/UdvegaLogo';

export default function VerifyEmail() {
  const { verifyEmailToken } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const email = params.get('email');

    if (!token || !email) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }

    verifyEmailToken(token, email).then((result) => {
      if (result.success) {
        setStatus('success');
        setMessage(result.message || 'Email verified successfully!');
        setTimeout(() => navigate('/dashboard'), 2500);
      } else {
        setStatus('error');
        setMessage(result.error || 'Verification failed.');
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #134E4A 100%)' }} data-testid="verify-email-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="glass-card rounded-3xl p-10 bg-white/80 text-center">
          <div className="flex justify-center mb-8">
            <UdvegaLogo size="md" />
          </div>

          {status === 'verifying' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-medium text-slate-800 mb-2">Verifying Your Email...</h2>
              <p className="text-sm text-slate-500">Please wait a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-teal-600" />
              </div>
              <h2 className="font-heading text-xl font-medium text-slate-800 mb-2">Email Verified!</h2>
              <p className="text-sm text-slate-500 mb-6">{message}</p>
              <p className="text-xs text-slate-400">Redirecting to dashboard...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="font-heading text-xl font-medium text-slate-800 mb-2">Verification Failed</h2>
              <p className="text-sm text-red-500 mb-6">{message}</p>
              <Link to="/login">
                <Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="back-to-login-btn">
                  Back to Login
                </Button>
              </Link>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
